import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ReservationImage from "../reservation-form.png";

const ReservationForm = () => {
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [course, setCourse] = useState("");
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const todayJst = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  const isTodayJst = (yyyyMmDd) =>
    yyyyMmDd &&
    yyyyMmDd ===
      new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Tokyo" });

  const nowJstHHMM = () =>
    new Date()
      .toLocaleTimeString("en-GB", { hour12: false, timeZone: "Asia/Tokyo" })
      .slice(0, 5);

  const filterPastTimesIfToday = (slots, selectedDate) => {
    if (!selectedDate) return slots;
    if (!isTodayJst(selectedDate)) return slots;
    const now = nowJstHHMM();
    return (slots || []).filter((t) => t >= now);
  };

  const fetchAvailableTimes = useCallback((selectedDate) => {
    if (!selectedDate) return;
    setLoading(true);

    const formattedDate = selectedDate;

    fetch(
      "https://smo9r2qi3f.execute-api.ap-northeast-1.amazonaws.com/prod/calendar",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: formattedDate }),
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then((data) => {
        const slots = data.free_slots || [];
        setAvailableTimes(filterPastTimesIfToday(slots, selectedDate));
      })
      .catch((err) => console.error("Error fetching available times:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setTime("");
    fetchAvailableTimes(date);
  }, [date, fetchAvailableTimes]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!date || !time) {
      alert("日付と時間を選択してください。");
      return;
    }
    const dtJst = new Date(`${date}T${time}:00+09:00`);
    const nowJst = new Date(
      new Date().toLocaleString("en-US", { timeZone: "Asia/Tokyo" })
    );
    if (dtJst < nowJst) {
      alert("過去の日時は選べません。日時を変更してください。");
      return;
    }

    const payload = { company, name, email, date, time, course };
    setSubmitting(true);

    fetch(
      "https://smo9r2qi3f.execute-api.ap-northeast-1.amazonaws.com/prod/reservation",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error("Reservation failed");
        return res.json();
      })
      .then(() => {
        navigate("/confirmation", { state: payload });
      })
      .catch((err) => console.error("Error:", err))
      .finally(() => setSubmitting(false));
  };

  if (submitting) {
    return (
      <div className="container">
        <p>予約確定中・・・</p>
        <progress style={{ width: "100%" }} />
      </div>
    );
  }

  return (
    <div className="container">
      <form onSubmit={handleSubmit} aria-label="予約フォーム">
        <img
          src={ReservationImage}
          alt="予約フォームのロゴ"
          className="form-header-image"
        />
        <div className="form-group">
          <label htmlFor="company">会社名</label>
          <input
            id="company"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="name">名前</label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">メールアドレス</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="date">日付</label>
          <input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            min={todayJst}
            onInvalid={(e) => {
              e.target.setCustomValidity(
                `日付は${todayJst}（本日）以降で選択してください`
              );
            }}
            onInput={(e) => {
              e.target.setCustomValidity("");
            }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="time">時間</label>
          {loading ? (
            <div className="loading">空いている時間を確認中…</div>
          ) : (
            <select
              id="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              required
              disabled={!availableTimes.length}
            >
              <option value="" disabled>
                {!date
                  ? "時間を選択してください"
                  : availableTimes.length
                  ? "時間を選択してください"
                  : "空いている時間がありません"}
              </option>
              {availableTimes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="course">打ち合わせ内容</label>
          <select
            id="course"
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            required
          >
            <option value="">--選択してください--</option>
            <option value="サービス・商品紹介">サービス・商品紹介</option>
            <option value="面接・商談">面接・商談</option>
            <option value="プロジェクト進捗確認">プロジェクト進捗確認</option>
            <option value="カスタマーサポート">カスタマーサポート</option>
            <option value="人材紹介・SES">人材紹介・SES</option>
            <option value="パートナーシップ・提携相談">
              パートナーシップ・提携相談
            </option>
            <option value="その他">その他</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={submitting || loading || !availableTimes.length}
        >
          予約する
        </button>
      </form>
    </div>
  );
};

export default ReservationForm;
